import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { GraduationCap, FileText, Search, MessageSquare, CheckCircle2, ArrowRight, Clock3, ShieldCheck, MapPin, Sparkles } from 'lucide-react';

const STUDENT_TESTIMONIALS = [
  { name: 'Ritika, Chandigarh', text: 'Posted requirement at night and got tutor calls next morning.' },
  { name: 'Aman, Mohali', text: 'Easy to compare tutors by subject and location.' },
  { name: 'Neha, Panchkula', text: 'Found a maths tutor quickly for Class 10 CBSE.' }
];

const CITY_QUICK_LINKS = ['Chandigarh', 'Mohali', 'Panchkula', 'Zirakpur', 'Kharar', 'Derabassi'];

export default function StudentsLandingPage({ user }) {
  const isStudent = ['student', 'parent', 'coaching', 'company'].includes(user?.role);
  const postRequirementLink = isStudent ? '/student/dashboard?open=post' : '/auth?role=student&mode=signup';

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-orange-50/40">
      <header className="bg-white/90 backdrop-blur border-b sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-indigo-600" />
            <span className="text-2xl font-outfit font-bold text-gray-900">Tricity Tutors</span>
          </Link>
        </nav>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-orange-200/40 blur-3xl" />
          <div className="absolute top-10 right-0 w-80 h-80 rounded-full bg-indigo-200/30 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 py-14 md:py-16 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <Badge className="mb-4 bg-orange-100 text-orange-700">Students & Parents</Badge>
            <h1 className="text-4xl md:text-5xl font-outfit font-bold text-gray-900 leading-tight tracking-tight">
              Post once. Get tutor calls fast.
            </h1>
            <p className="mt-4 text-gray-600 text-lg max-w-xl">
              Share your requirement and get matched tutors from Chandigarh, Mohali, Panchkula and Zirakpur.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 items-center">
              <Link to={postRequirementLink}>
                <Button size="lg" className="rounded-full bg-orange-500 hover:bg-orange-600 shadow-xl shadow-orange-200 h-14 px-10 text-base font-extrabold tracking-wide">
                  Post Your Requirement for free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="mt-4 max-w-md rounded-lg border border-orange-200 bg-orange-50 px-4 py-3">
              <p className="flex items-center gap-2 text-base font-semibold text-orange-800">
                <Clock3 className="w-5 h-5 text-orange-600" />
                Post in less than 1 minute
              </p>
              <p className="text-sm text-orange-700/90 mt-1">No long forms. Just basic details and submit.</p>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-gray-600">
              <div className="flex items-center gap-2 bg-white/80 border rounded-full px-3 py-2"><Clock3 className="w-4 h-4 text-orange-500" />Quick responses</div>
              <div className="flex items-center gap-2 bg-white/80 border rounded-full px-3 py-2"><ShieldCheck className="w-4 h-4 text-emerald-600" />Verified profiles</div>
              <div className="flex items-center gap-2 bg-white/80 border rounded-full px-3 py-2"><MapPin className="w-4 h-4 text-indigo-600" />Local tutors</div>
            </div>
            <div className="mt-5 flex items-center gap-2 text-sm text-gray-700">
              <Sparkles className="w-4 h-4 text-indigo-600" />
              <span>Best for class-wise and subject-wise tutor matches.</span>
            </div>
          </div>
          <Card className="border-orange-200 shadow-xl bg-white">
            <CardHeader>
              <CardTitle>3 Simple Steps</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-gray-700">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-orange-500 mt-0.5" />
                <p><span className="font-semibold">Post need:</span> Subject, class, budget, location.</p>
              </div>
              <div className="flex items-start gap-3">
                <Search className="w-5 h-5 text-indigo-600 mt-0.5" />
                <p><span className="font-semibold">Get matched:</span> Relevant tutors respond.</p>
              </div>
              <div className="flex items-start gap-3">
                <MessageSquare className="w-5 h-5 text-emerald-600 mt-0.5" />
                <p><span className="font-semibold">Start classes:</span> Chat and finalize quickly.</p>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mb-2" />
              <h3 className="font-semibold">Strong Local Coverage</h3>
              <p className="text-sm text-gray-600 mt-1">Tricity + nearby areas.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mb-2" />
              <h3 className="font-semibold">Home + Online</h3>
              <p className="text-sm text-gray-600 mt-1">Choose what fits your schedule.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mb-2" />
              <h3 className="font-semibold">Easy Tracking</h3>
              <p className="text-sm text-gray-600 mt-1">See your posted requirements in dashboard.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-12">
        <h2 className="text-xl font-outfit font-bold text-gray-900 mb-4">What parents say</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {STUDENT_TESTIMONIALS.map((item) => (
            <Card key={item.name} className="border-gray-200">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-700">&ldquo;{item.text}&rdquo;</p>
                <p className="text-xs text-gray-500 mt-3">{item.name}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24 md:pb-12">
        <h2 className="text-xl font-outfit font-bold text-gray-900 mb-4">Find tutors by city</h2>
        <div className="flex flex-wrap gap-2">
          {CITY_QUICK_LINKS.map((city) => (
            <Link key={city} to={`/tutors?location=${encodeURIComponent(city)}`}>
              <Badge className="bg-white border text-gray-700 hover:bg-orange-50 cursor-pointer">{city}</Badge>
            </Link>
          ))}
        </div>
      </section>

    </div>
  );
}
